import { memo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { DiscardCardIcon, SupportStrokeIcon } from "../assets/icons";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useClaimLink } from "../hooks/useClaimLink";
import { useExitToPartnerOrLanding } from "../hooks/useExitToPartnerOrLanding";
import { getEvents, safeCapture } from "../analytics";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";

/**
 * Confirmation after Discard. The money request is already cancelled.
 * Return to app only when the claim envelope has an http(s) callbackUrl.
 * Reopening the link hits the cancelled-request gate, not this screen.
 */
export const IntentionDiscardPage = memo(() => {
  useClaimLink();
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session, updateSession } = useSessionStore();
  const { canExitLive, liveCallbackUrl } = useExitToPartnerOrLanding();
  const location = useLocation();

  useEffect(() => {
    const discarded = (location.state as { discarded?: boolean } | null)?.discarded;
    if (!discarded) return;
    const current = useSessionStore.getState().session.data;
    if (!current?.config || current.config.requestStatus === "cancelled") return;
    updateSession({
      ...current,
      config: { ...current.config, requestStatus: "cancelled" },
    });
  }, [location.state, updateSession]);

  const colorConfig = theme.errorView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    iconColors,
    backgroundIcon,
  } = colorConfig;

  const userData = session.data?.userData;
  const requesterName =
    [userData?.name?.firstName, userData?.name?.lastName]
      .filter(Boolean)
      .join(" ") ||
    userData?.fullName ||
    t("fundRequest.someone");

  useEffect(() => {
    safeCapture(getEvents().INTENTION_DISCARD_VIEW);
  }, []);

  const handleExit = () => {
    if (!liveCallbackUrl) return;
    safeCapture(getEvents().EXIT_CLICK, { screen: "intention_discard" });
    window.location.href = liveCallbackUrl;
  };

  const { openSupport, supportSheet } = useSupportSheet();

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="need-help-header flow-landing-page">
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            noBackButton
            color={title}
            onIconClick={() => openSupport()}
            icon={
              <SupportStrokeIcon
                size={35}
                colors={[backgroundIcon, backgroundIcon]}
                glyphColor={iconColors[0]}
              />
            }
          />
        </Layout.Header>
        <Layout.Content className="landing-section flow-hero-section flow-hero-section--with-header">
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />
          <section className="flow-hero">
            <FlowHeroMark backgroundIcon={backgroundIcon}>
              <DiscardCardIcon size={60} colors={iconColors} />
            </FlowHeroMark>
            <section className="landing-container">
              <PageTitle
                highlight={t("fundRequest.discardScreen.highlight")}
                highlightColor={title}
                secudnary={t("fundRequest.discardScreen.secondary")}
                secudnaryColor={title}
              />
              <BodyCopy textColor={bodyCopy}>
                {t("fundRequest.discardScreen.body", { name: requesterName })}
              </BodyCopy>
            </section>
          </section>
        </Layout.Content>
        {canExitLive && (
          <Layout.Footer>
            <section className="footer-section">
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                onClick={handleExit}
              >
                {t("fundRequest.discardScreen.cta")}
              </Button>
            </section>
          </Layout.Footer>
        )}
      </AuraLayout>
      {supportSheet}
    </>
  );
});
